import { useCrawlProgress } from '../hooks/useCrawlProgress';
import { ProgressBar } from './ProgressBar';
import { Loading } from './Loading';

interface CrawlProgressProps {
  novelId: string;
}

const statusText: Record<string, string> = {
  pending: '等待中...',
  crawling: '下載中...',
  completed: '下載完成',
  failed: '下載失敗',
  paused: '已暫停'
};

const statusColor: Record<string, string> = {
  pending: 'text-gray-500',
  crawling: 'text-blue-500',
  completed: 'text-green-500',
  failed: 'text-red-500',
  paused: 'text-yellow-500'
};

export function CrawlProgress({ novelId }: CrawlProgressProps) {
  const { progress, isPolling, error } = useCrawlProgress(novelId);

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-600">
        載入進度失敗: {error}
      </div>
    );
  }

  if (!progress) {
    return <Loading text="載入進度中..." />;
  }

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        {isPolling && progress.status === 'crawling' && (
          <span className="pulse-dot" />
        )}
        <span className={`font-medium ${statusColor[progress.status]}`}>
          {statusText[progress.status]}
        </span>
      </div>

      <ProgressBar
        value={progress.crawledChapters}
        max={progress.totalChapters}
        label={`${progress.crawledChapters} / ${progress.totalChapters} 章`}
      />

      {progress.currentChapter && (
        <p className="text-sm text-gray-600">
          正在下載: <span className="font-medium">{progress.currentChapter}</span>
        </p>
      )}

      {progress.isStalled && progress.status === 'crawling' && (
        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md text-sm text-yellow-700 dark:text-yellow-400">
          此章節已卡住 5 分鐘以上，可到章節列表點「重新抓取」或「跳過」該章節
        </div>
      )}

      {progress.failedChapters > 0 && (
        <p className="text-sm text-red-500">
          失敗章節: {progress.failedChapters}（可在章節列表中重試或跳過）
        </p>
      )}
    </div>
  );
}
